import { useEffect, useState } from "react";
import { useApi } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Mail } from "lucide-react";
import { toast } from "sonner";

/**
 * Shared Complimentary Codes panel used by admin (full CRUD) and organiser
 * (read-only redeemed list) views.
 */
export default function ComplimentaryCodesPanel({ eventId, mode = "admin" }) {
  const api = useApi();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);
  const [emailTo, setEmailTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [provincePreview, setProvincePreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  // Middlesex two-step selection: first user picks Craft / Royal Arch,
  // then the preview modal opens with the per-section totals.
  const [middlesexPicker, setMiddlesexPicker] = useState(false);
  const [middlesexPick, setMiddlesexPick] = useState({ craft: true, royal_arch: true });
  const [middlesexPreview, setMiddlesexPreview] = useState(null);
  // Recipient email preview (Phase 4)
  const [emailPreview, setEmailPreview] = useState(null);
  const [emailPreviewLoading, setEmailPreviewLoading] = useState(null);
  const [emailSending, setEmailSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const path = mode === "admin"
        ? `/admin/events/${eventId}/complimentary-codes`
        : `/organiser/events/${eventId}/complimentary-codes`;
      const r = await api(path);
      const d = await r.json();
      if (r.ok) setCodes(d.codes || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [eventId, mode]);

  const generate = async () => {
    if (qty < 1) return;
    setBusy(true);
    try {
      const r = await api(`/admin/events/${eventId}/complimentary-codes/generate`, {
        method: "POST", body: JSON.stringify({ quantity: qty }),
      });
      const d = await r.json();
      if (!r.ok) return toast.error(d.detail || "Generation failed");
      toast.success(`${d.generated} new code${d.generated === 1 ? "" : "s"} generated.`);
      load();
    } finally { setBusy(false); }
  };

  const openProvincePreview = async () => {
    setPreviewLoading(true);
    try {
      const r = await api(`/admin/events/${eventId}/complimentary-codes/generate-province/preview`);
      const d = await r.json();
      if (!r.ok) return toast.error(d.detail || "Could not load preview");
      if (!d.sources || d.sources.length === 0) {
        toast.error("No Province / District rows have a complimentary ticket count set.");
        return;
      }
      setProvincePreview(d);
    } finally { setPreviewLoading(false); }
  };

  const generateProvince = async () => {
    setBusy(true);
    try {
      const r = await api(`/admin/events/${eventId}/complimentary-codes/generate-province`, {
        method: "POST", body: JSON.stringify({}),
      });
      const d = await r.json();
      if (!r.ok) return toast.error(d.detail || "Generation failed");
      toast.success(
        `${d.generated} code${d.generated === 1 ? "" : "s"} generated across ${d.sources} Province/District row${d.sources === 1 ? "" : "s"}.`,
      );
      setProvincePreview(null);
      load();
    } finally { setBusy(false); }
  };

  const openMiddlesexPicker = () => {
    setMiddlesexPick({ craft: true, royal_arch: true });
    setMiddlesexPicker(true);
  };

  const loadMiddlesexPreview = async () => {
    if (!middlesexPick.craft && !middlesexPick.royal_arch) {
      toast.error("Select at least one Middlesex list.");
      return;
    }
    setPreviewLoading(true);
    try {
      const qs = new URLSearchParams({
        include_craft: String(!!middlesexPick.craft),
        include_royal_arch: String(!!middlesexPick.royal_arch),
      });
      const r = await api(
        `/admin/events/${eventId}/complimentary-codes/generate-middlesex/preview?${qs}`,
      );
      const d = await r.json();
      if (!r.ok) return toast.error(d.detail || "Could not load preview");
      if (!d.total) {
        toast.error("The selected Middlesex list(s) have no active members.");
        return;
      }
      setMiddlesexPicker(false);
      setMiddlesexPreview(d);
    } finally { setPreviewLoading(false); }
  };

  const generateMiddlesex = async () => {
    setBusy(true);
    try {
      const r = await api(
        `/admin/events/${eventId}/complimentary-codes/generate-middlesex`,
        {
          method: "POST",
          body: JSON.stringify({
            include_craft: !!middlesexPick.craft,
            include_royal_arch: !!middlesexPick.royal_arch,
          }),
        },
      );
      const d = await r.json();
      if (!r.ok) return toast.error(d.detail || "Generation failed");
      const perSection = (d.sections || [])
        .map((s) => `${s.count} ${s.label}`)
        .join(" · ");
      toast.success(`${d.generated} code${d.generated === 1 ? "" : "s"} generated (${perSection}).`);
      setMiddlesexPreview(null);
      load();
    } finally { setBusy(false); }
  };

  const openEmailPreview = async (code) => {
    setEmailPreviewLoading(code.code_id);
    try {
      const r = await api(
        `/admin/events/${eventId}/complimentary-codes/${code.code_id}/email-preview`,
      );
      const d = await r.json();
      if (!r.ok) return toast.error(d.detail || "Could not load email preview");
      setEmailPreview(d);
    } finally { setEmailPreviewLoading(null); }
  };

  const sendRecipientEmail = async () => {
    if (!emailPreview) return;
    setEmailSending(true);
    try {
      const r = await api(
        `/admin/events/${eventId}/complimentary-codes/${emailPreview.code_id}/email-send`,
        { method: "POST", body: JSON.stringify({}) },
      );
      const d = await r.json();
      if (!r.ok) return toast.error(d.detail || "Send failed");
      toast.success(
        `Sent to ${d.sent_to} (${(d.codes_emailed || []).length} code${(d.codes_emailed || []).length === 1 ? "" : "s"} stamped).`,
      );
      setEmailPreview(null);
      load();
    } finally { setEmailSending(false); }
  };

  const emailCsv = async () => {
    if (!emailTo.trim()) return toast.error("Enter a recipient email");
    setBusy(true);
    try {
      const r = await api(`/admin/events/${eventId}/complimentary-codes/email-csv`, {
        method: "POST", body: JSON.stringify({ to: emailTo.trim().toLowerCase() }),
      });
      const d = await r.json();
      if (!r.ok) return toast.error(d.detail || "Email failed");
      toast.success(`CSV emailed to ${d.to} (${d.row_count} rows).`);
    } finally { setBusy(false); }
  };

  return (
    <Card className="p-5 mt-6" data-testid="comp-codes-panel">
      <h3 className="font-semibold">Complimentary codes {mode === "admin" ? "(all)" : "(redeemed only)"}</h3>
      <p className="text-xs text-muted-foreground mt-1">
        {mode === "admin"
          ? "Generate new codes for the event. Available (unused) codes stay hidden from organisers by design."
          : "Redeemed codes for this event. Available codes are hidden by design."}
      </p>

      {mode === "admin" && (
        <div className="mt-4 space-y-4">
          <div className="grid gap-3 md:grid-cols-3">
            <div className="space-y-1">
              <Label>Quantity</Label>
              <div className="flex gap-2 items-center">
                <Input type="number" min={1} max={200} value={qty}
                       onChange={(e) => setQty(Math.max(1, Math.min(200, parseInt(e.target.value || "1", 10))))}
                       className="w-20 text-center" data-testid="comp-qty" />
                <Button
                  onClick={generate}
                  disabled={busy}
                  data-testid="comp-generate"
                  className="flex-1 bg-slate-900 text-white hover:bg-red-600 hover:text-white"
                >
                  {busy ? "Working…" : "Generate Generic Codes"}
                </Button>
              </div>
            </div>
            <div className="space-y-1">
              <Label>&nbsp;</Label>
              <Button
                onClick={openProvincePreview}
                disabled={busy || previewLoading}
                data-testid="comp-generate-province"
                className="w-full bg-slate-900 text-white hover:bg-red-600 hover:text-white"
              >
                {previewLoading ? "Loading…" : "Generate Province/District Codes"}
              </Button>
              <p className="text-xs text-muted-foreground mt-1">
                Reads allocations from the Other Provinces / Districts master list.
              </p>
            </div>
            <div className="space-y-1">
              <Label>&nbsp;</Label>
              <Button
                onClick={openMiddlesexPicker}
                disabled={busy}
                data-testid="comp-generate-middlesex"
                className="w-full bg-slate-900 text-white hover:bg-red-600 hover:text-white"
              >
                Generate Province of Middlesex Codes
              </Button>
              <p className="text-xs text-muted-foreground mt-1">
                One code per active member of Middlesex Craft &amp; Royal Arch.
              </p>
            </div>
          </div>
          <div className="space-y-1 max-w-md">
            <Label>Email CSV</Label>
            <div className="flex gap-2 items-center">
              <Input type="email" placeholder="recipient@example.com" value={emailTo}
                     onChange={(e) => setEmailTo(e.target.value)} data-testid="comp-email-to" />
              <Button variant="outline" onClick={emailCsv} disabled={busy} data-testid="comp-email-csv">
                Email CSV
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-muted-foreground border-b border-border">
              <th className="py-2 pr-3">Code</th>
              <th className="py-2 pr-3">Status</th>
              <th className="py-2 pr-3">Attendee</th>
              <th className="py-2 pr-3">Booking</th>
              <th className="py-2 pr-3">Redeemed at</th>
              <th className="py-2 pr-3">Linked To</th>
              <th className="py-2 pr-3">Province</th>
              <th className="py-2 pr-3">Last emailed</th>
              {mode === "admin" && <th className="py-2 pr-3"></th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={mode === "admin" ? 9 : 8} className="py-4 text-muted-foreground">Loading…</td></tr>
            ) : codes.length === 0 ? (
              <tr><td colSpan={mode === "admin" ? 9 : 8} className="py-4 text-muted-foreground italic">
                {mode === "admin" ? "No codes yet. Generate some above." : "No redeemed codes yet."}
              </td></tr>
            ) : codes.map((c) => {
              const canEmail = !!(c.linked_to && c.province);
              return (
                <tr key={c.code} className="border-b border-border/60">
                  <td className="py-2 pr-3 font-mono">{c.code}</td>
                  <td className="py-2 pr-3">{c.status}</td>
                  <td className="py-2 pr-3">{c.attendee_name || "—"}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{c.booking_id || "—"}</td>
                  <td className="py-2 pr-3">{c.redeemed_at ? new Date(c.redeemed_at).toLocaleString("en-GB") : "—"}</td>
                  <td className="py-2 pr-3">{c.linked_to || "—"}</td>
                  <td className="py-2 pr-3">{c.province || "—"}</td>
                  <td className="py-2 pr-3 text-xs">
                    {c.last_emailed_at ? new Date(c.last_emailed_at).toLocaleString("en-GB") : "—"}
                  </td>
                  {mode === "admin" && (
                    <td className="py-2 pr-3">
                      {canEmail ? (
                        <button
                          type="button"
                          onClick={() => openEmailPreview(c)}
                          disabled={emailPreviewLoading === c.code_id}
                          className="p-1 rounded hover:bg-red-50 disabled:opacity-50"
                          title={c.last_emailed_at ? "Re-send email" : "Send email"}
                          aria-label="Send email"
                          data-testid={`comp-email-icon-${c.code_id}`}
                        >
                          <Mail size={16} className="text-red-600" />
                        </button>
                      ) : null}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={!!provincePreview} onOpenChange={(o) => { if (!o && !busy) setProvincePreview(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Generate Province / District Codes</DialogTitle>
            <DialogDescription>
              Review the allocations below. On Proceed, unique codes will be
              generated for every row and emailed to you as a CSV.
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm">
            <div className="mb-2">
              <span className="text-muted-foreground">Total codes to be generated: </span>
              <span className="font-semibold" data-testid="province-preview-total">
                {provincePreview?.total ?? 0}
              </span>
              <span className="text-muted-foreground"> across </span>
              <span className="font-semibold">{provincePreview?.sources?.length ?? 0}</span>
              <span className="text-muted-foreground"> Province / District row{(provincePreview?.sources?.length ?? 0) === 1 ? "" : "s"}.</span>
            </div>
            {provincePreview?.over_batch_cap && (
              <div className="mb-2 text-red-600">
                Warning: this exceeds the per-batch cap of {provincePreview.batch_cap}. Reduce
                allocations or split the run.
              </div>
            )}
            <div className="max-h-80 overflow-auto border rounded">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 sticky top-0">
                  <tr className="text-left">
                    <th className="py-2 px-3">Province / District</th>
                    <th className="py-2 px-3">Linked To</th>
                    <th className="py-2 px-3">Short label</th>
                    <th className="py-2 px-3 text-right">Codes</th>
                  </tr>
                </thead>
                <tbody>
                  {provincePreview?.sources?.map((s) => (
                    <tr key={s.entry_id} className="border-t">
                      <td className="py-2 px-3">{s.prov_lc_name || s.province}</td>
                      <td className="py-2 px-3">{s.linked_to || "—"}</td>
                      <td className="py-2 px-3">{s.short_label || "—"}</td>
                      <td className="py-2 px-3 text-right font-mono">{s.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setProvincePreview(null)} disabled={busy}
                    data-testid="province-preview-cancel">
              Cancel
            </Button>
            <Button onClick={generateProvince}
                    disabled={busy || !!provincePreview?.over_batch_cap}
                    data-testid="province-preview-proceed">
              {busy ? "Generating…" : `Proceed — generate ${provincePreview?.total ?? 0} code${(provincePreview?.total ?? 0) === 1 ? "" : "s"}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Middlesex — step 1: pick Craft / Royal Arch */}
      <Dialog open={middlesexPicker} onOpenChange={(o) => { if (!o && !previewLoading) setMiddlesexPicker(false); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate Province of Middlesex Codes</DialogTitle>
            <DialogDescription>
              Choose which Middlesex list(s) to include. One code is generated
              per active member (1:1).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <label className="flex items-center gap-2 select-none">
              <input type="checkbox" checked={middlesexPick.craft}
                     onChange={(e) => setMiddlesexPick((p) => ({ ...p, craft: e.target.checked }))}
                     data-testid="middlesex-pick-craft" />
              Middlesex Craft — <span className="text-muted-foreground">PGL Middlesex</span>
            </label>
            <label className="flex items-center gap-2 select-none">
              <input type="checkbox" checked={middlesexPick.royal_arch}
                     onChange={(e) => setMiddlesexPick((p) => ({ ...p, royal_arch: e.target.checked }))}
                     data-testid="middlesex-pick-royal-arch" />
              Middlesex Royal Arch — <span className="text-muted-foreground">PGC Middlesex</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setMiddlesexPicker(false)} disabled={previewLoading}>
              Cancel
            </Button>
            <Button onClick={loadMiddlesexPreview}
                    disabled={previewLoading || (!middlesexPick.craft && !middlesexPick.royal_arch)}
                    data-testid="middlesex-pick-continue">
              {previewLoading ? "Loading…" : "Continue"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Middlesex — step 2: review + proceed */}
      <Dialog open={!!middlesexPreview} onOpenChange={(o) => { if (!o && !busy) setMiddlesexPreview(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Confirm Middlesex Codes</DialogTitle>
            <DialogDescription>
              One code per active member. Proceed generates the codes and emails
              you a CSV.
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm">
            <div className="mb-2">
              <span className="text-muted-foreground">Total codes to be generated: </span>
              <span className="font-semibold" data-testid="middlesex-preview-total">
                {middlesexPreview?.total ?? 0}
              </span>
            </div>
            {middlesexPreview?.over_batch_cap && (
              <div className="mb-2 text-red-600">
                Warning: exceeds the per-batch cap of {middlesexPreview.batch_cap}.
              </div>
            )}
            <div className="max-h-80 overflow-auto border rounded">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 sticky top-0">
                  <tr className="text-left">
                    <th className="py-2 px-3">Section</th>
                    <th className="py-2 px-3">Province label</th>
                    <th className="py-2 px-3 text-right">Codes</th>
                  </tr>
                </thead>
                <tbody>
                  {middlesexPreview?.sections?.map((s) => (
                    <tr key={s.key} className="border-t">
                      <td className="py-2 px-3">{s.label}</td>
                      <td className="py-2 px-3">{s.province_label}</td>
                      <td className="py-2 px-3 text-right font-mono">{s.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setMiddlesexPreview(null)} disabled={busy}
                    data-testid="middlesex-preview-cancel">
              Cancel
            </Button>
            <Button onClick={generateMiddlesex}
                    disabled={busy || !!middlesexPreview?.over_batch_cap || !(middlesexPreview?.total)}
                    data-testid="middlesex-preview-proceed">
              {busy ? "Generating…" : `Proceed — generate ${middlesexPreview?.total ?? 0} code${(middlesexPreview?.total ?? 0) === 1 ? "" : "s"}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recipient email preview (Phase 4) */}
      <Dialog open={!!emailPreview} onOpenChange={(o) => { if (!o && !emailSending) setEmailPreview(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Send complimentary email</DialogTitle>
            <DialogDescription>
              {emailPreview?.code_count > 1
                ? `${emailPreview?.code_count} codes will be merged into a single email to this recipient.`
                : "One code will be emailed to this recipient."}
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm space-y-2">
            {emailPreview?.template_missing && (
              <div className="p-2 rounded border border-red-500 bg-red-50 text-red-700 text-xs">
                The email template for this list is empty. Edit it under
                Admin → Email templates before sending.
              </div>
            )}
            <div className="grid grid-cols-[80px_1fr] gap-y-1 gap-x-3">
              <span className="text-muted-foreground">From</span>
              <span className="font-mono text-xs">{emailPreview?.from || "(server default)"}</span>
              <span className="text-muted-foreground">To</span>
              <span className="font-mono text-xs" data-testid="email-preview-to">
                {emailPreview?.to || <em className="text-red-600 not-italic">missing</em>} — {emailPreview?.full_name}
              </span>
              <span className="text-muted-foreground">Codes</span>
              <span className="font-mono text-xs" data-testid="email-preview-codes">
                {emailPreview?.codes_merged}
              </span>
              <span className="text-muted-foreground">Subject</span>
              <span className="font-semibold" data-testid="email-preview-subject">
                {emailPreview?.subject || <em className="text-muted-foreground not-italic">(empty)</em>}
              </span>
            </div>
            <div className="mt-2 border rounded p-3 max-h-72 overflow-auto bg-white"
                 data-testid="email-preview-body"
                 dangerouslySetInnerHTML={{ __html: emailPreview?.body_html || "" }} />
            {emailPreview?.last_emailed_at && (
              <div className="text-xs text-muted-foreground">
                Last sent: {new Date(emailPreview.last_emailed_at).toLocaleString("en-GB")}. Sending again will refresh this timestamp on all affected codes.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEmailPreview(null)} disabled={emailSending}
                    data-testid="email-preview-cancel">
              Cancel
            </Button>
            <Button
              onClick={sendRecipientEmail}
              disabled={emailSending || !emailPreview?.to || !emailPreview?.subject}
              data-testid="email-preview-send"
              className="bg-slate-900 text-white hover:bg-red-600 hover:text-white"
            >
              {emailSending ? "Sending…" : `Send${emailPreview?.code_count > 1 ? ` (${emailPreview?.code_count} codes)` : ""}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
